import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  Box,
  BarChart3,
  Layers,
  Code,
  Play,
  Eye,
  Download,
  Settings,
  Plus,
  Trash2,
  ChevronDown,
  ChevronRight,
  Sparkles,
  Clock,
  TrendingUp,
  Hash,
  Calendar,
  DollarSign,
  Database,
  Zap,
  Target,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';
import { DiscoveredSchema, SchemaField } from './SchemaDiscovery';
import { useToast } from '@/hooks/use-toast';

export interface CubeMeasure {
  name: string;
  type: 'count' | 'sum' | 'avg' | 'min' | 'max' | 'countDistinct' | 'number';
  sql?: string;
  title?: string;
  description?: string;
  format?: string;
  drillMembers?: string[];
  filters?: Array<{
    sql: string;
    description?: string;
  }>;
  rolling?: {
    window: string;
    offset?: string;
  };
}

export interface CubeDimension {
  name: string;
  type: 'string' | 'number' | 'boolean' | 'time' | 'geo';
  sql?: string;
  title?: string;
  description?: string;
  primaryKey?: boolean;
  shown?: boolean;
  case?: {
    when: Array<{
      sql: string;
      label: string;
    }>;
    else?: {
      label: string;
    };
  };
}

export interface CubePreAggregation {
  name: string;
  measures: string[];
  dimensions: string[];
  timeDimension?: string;
  granularity?: 'second' | 'minute' | 'hour' | 'day' | 'week' | 'month' | 'quarter' | 'year';
  partitionGranularity?: 'day' | 'week' | 'month' | 'year';
  refreshKey?: {
    every?: string;
    sql?: string;
  };
  buildRangeStart?: {
    sql: string;
  };
  buildRangeEnd?: {
    sql: string;
  };
  scheduledRefresh?: boolean;
}

export interface CubeSchema {
  name: string;
  sql: string;
  title?: string;
  description?: string;
  measures: CubeMeasure[];
  dimensions: CubeDimension[];
  preAggregations?: CubePreAggregation[];
  joins?: Array<{
    name: string;
    sql: string;
    relationship: 'belongsTo' | 'hasMany' | 'hasOne';
  }>;
  segments?: Array<{
    name: string;
    sql: string;
    title?: string;
    description?: string;
  }>;
}

interface CubeSchemaBuilderProps {
  discoveredSchema: DiscoveredSchema;
  onSchemaGenerated?: (cubeSchema: CubeSchema) => void;
  onPreview?: (schema: CubeSchema) => void;
  industryContext?: string;
  optimizationLevel?: 'performance' | 'accuracy' | 'balanced';
}

export const CubeSchemaBuilder: React.FC<CubeSchemaBuilderProps> = ({
  discoveredSchema,
  onSchemaGenerated,
  onPreview,
  industryContext = 'general',
  optimizationLevel = 'balanced'
}) => {
  const [selectedTable, setSelectedTable] = useState<string>(discoveredSchema.tables[0]?.name || '');
  const [cubeSchema, setCubeSchema] = useState<CubeSchema | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState('measures');
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['basic']));
  const { toast } = useToast();

  const generateCubeSchema = async () => {
    if (!selectedTable) return;

    setIsGenerating(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 2000)); // Simulate AI generation

      const table = discoveredSchema.tables.find(t => t.name === selectedTable);
      if (!table) return;

      const generatedSchema = generateSchemaForTable(table);
      setCubeSchema(generatedSchema);

      toast({
        title: "Cube Schema Generated",
        description: `Generated ${generatedSchema.measures.length} measures and ${generatedSchema.dimensions.length} dimensions`,
      });

      onSchemaGenerated?.(generatedSchema);
    } catch (error) {
      toast({
        title: "Generation Failed",
        description: "Failed to generate Cube.js schema",
        variant: "destructive"
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const generateSchemaForTable = (table: any): CubeSchema => {
    const measures: CubeMeasure[] = [
      {
        name: 'count',
        type: 'count',
        title: `Total ${table.suggestedBusinessName || table.name}`,
        description: `Total number of ${table.name} records`
      }
    ];

    // Add measures for numeric fields
    table.fields.forEach((field: SchemaField) => {
      if (field.type.toLowerCase().includes('int') ||
          field.type.toLowerCase().includes('decimal') ||
          field.type.toLowerCase().includes('float')) {

        if (!field.isPrimary && !field.isForeign) {
          measures.push({
            name: `${field.name}_sum`,
            type: 'sum',
            sql: field.name,
            title: `Total ${field.suggestedBusinessName || field.name}`,
            description: `Sum of ${field.name} values`
          });

          measures.push({
            name: `${field.name}_avg`,
            type: 'avg',
            sql: field.name,
            title: `Average ${field.suggestedBusinessName || field.name}`,
            description: `Average of ${field.name} values`
          });
        }
      }
    });

    const dimensions: CubeDimension[] = table.fields.map((field: SchemaField) => ({
      name: field.name,
      type: inferDimensionType(field.type),
      sql: field.name,
      title: field.suggestedBusinessName || field.name,
      description: field.description,
      primaryKey: field.isPrimary,
      shown: !field.isPrimary && !field.isForeign
    }));

    // Generate pre-aggregations for performance
    const preAggregations: CubePreAggregation[] = [];

    const timeField = table.fields.find((f: SchemaField) =>
      f.type.toLowerCase().includes('date') || f.type.toLowerCase().includes('time')
    );

    if (timeField && optimizationLevel === 'performance') {
      preAggregations.push({
        name: 'main',
        measures: measures.slice(0, 3).map(m => m.name),
        dimensions: dimensions.filter(d => d.shown).slice(0, 5).map(d => d.name),
        timeDimension: timeField.name,
        granularity: 'day',
        refreshKey: {
          every: '1 hour'
        },
        scheduledRefresh: true
      });
    }

    return {
      name: table.name,
      sql: `SELECT * FROM ${table.name}`,
      title: table.suggestedBusinessName || table.name,
      description: table.description,
      measures,
      dimensions,
      preAggregations: preAggregations.length > 0 ? preAggregations : undefined
    };
  };

  const inferDimensionType = (sqlType: string): CubeDimension['type'] => {
    const lowerType = sqlType.toLowerCase();
    if (lowerType.includes('date') || lowerType.includes('time')) return 'time';
    if (lowerType.includes('int') || lowerType.includes('decimal') || lowerType.includes('float')) return 'number';
    if (lowerType.includes('bool')) return 'boolean';
    return 'string';
  };

  const addMeasure = () => {
    if (!cubeSchema) return;

    const newMeasure: CubeMeasure = {
      name: `new_measure_${cubeSchema.measures.length + 1}`,
      type: 'count',
      title: 'New Measure',
      description: 'Custom measure'
    };

    setCubeSchema({
      ...cubeSchema,
      measures: [...cubeSchema.measures, newMeasure]
    });
  };

  const addDimension = () => {
    if (!cubeSchema) return;

    const newDimension: CubeDimension = {
      name: `new_dimension_${cubeSchema.dimensions.length + 1}`,
      type: 'string',
      title: 'New Dimension',
      description: 'Custom dimension'
    };

    setCubeSchema({
      ...cubeSchema,
      dimensions: [...cubeSchema.dimensions, newDimension]
    });
  };

  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  const generateSQLPreview = () => {
    if (!cubeSchema) return '';

    return `cube(\`${cubeSchema.name}\`, {
  sql: \`${cubeSchema.sql}\`,

  measures: {
${cubeSchema.measures.map(m => `    ${m.name}: {
      type: \`${m.type}\`,
      ${m.sql ? `sql: \`${m.sql}\`,` : ''}
      ${m.title ? `title: \`${m.title}\`,` : ''}
    }`).join(',\n\n')}
  },

  dimensions: {
${cubeSchema.dimensions.map(d => `    ${d.name}: {
      type: \`${d.type}\`,
      ${d.sql ? `sql: \`${d.sql}\`,` : ''}
      ${d.title ? `title: \`${d.title}\`,` : ''}
      ${d.primaryKey ? `primaryKey: true,` : ''}
    }`).join(',\n\n')}
  }${cubeSchema.preAggregations && cubeSchema.preAggregations.length > 0 ? `,

  preAggregations: {
${cubeSchema.preAggregations.map(p => `    ${p.name}: {
      measures: [${p.measures.map(m => `\`${m}\``).join(', ')}],
      dimensions: [${p.dimensions.map(d => `\`${d}\``).join(', ')}],
      ${p.timeDimension ? `timeDimension: \`${p.timeDimension}\`,` : ''}
      ${p.granularity ? `granularity: \`${p.granularity}\`,` : ''}
      ${p.refreshKey ? `refreshKey: { every: \`${p.refreshKey.every}\` },` : ''}
    }`).join(',\n\n')}
  }` : ''}
});`;
  };

  const getMeasureIcon = (type: CubeMeasure['type']) => {
    switch (type) {
      case 'count': return <Hash className="h-4 w-4" />;
      case 'sum': case 'avg': return <TrendingUp className="h-4 w-4" />;
      case 'min': case 'max': return <Target className="h-4 w-4" />;
      default: return <BarChart3 className="h-4 w-4" />;
    }
  };

  const getDimensionIcon = (type: CubeDimension['type']) => {
    switch (type) {
      case 'time': return <Calendar className="h-4 w-4" />;
      case 'number': return <Hash className="h-4 w-4" />;
      case 'boolean': return <CheckCircle className="h-4 w-4" />;
      default: return <Layers className="h-4 w-4" />;
    }
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Box className="h-5 w-5" />
          Cube.js Schema Builder
          <Badge variant="secondary" className="ml-2 flex items-center gap-1">
            <Sparkles className="h-3 w-3" />
            AI Generated
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!cubeSchema ? (
          <div className="space-y-6">
            {/* Table Selection */}
            <div className="space-y-3">
              <Label>Select Table for Cube Schema</Label>
              <Select value={selectedTable} onValueChange={setSelectedTable}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a table" />
                </SelectTrigger>
                <SelectContent>
                  {discoveredSchema.tables.map((table) => (
                    <SelectItem key={table.name} value={table.name}>
                      <div className="flex items-center gap-2">
                        <Database className="h-4 w-4" />
                        <span>{table.suggestedBusinessName || table.name}</span>
                        <Badge variant="outline" className="text-xs">
                          {table.fields.length} fields
                        </Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Configuration Options */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Generation Options</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Industry Context</Label>
                    <Badge variant="outline">{industryContext}</Badge>
                  </div>
                  <div className="space-y-2">
                    <Label>Optimization Level</Label>
                    <Badge variant="outline">{optimizationLevel}</Badge>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>What will be generated:</Label>
                  <div className="text-sm space-y-1 text-gray-600 dark:text-gray-400">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      Automatic measures (count, sum, avg) for numeric fields
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      Dimensions for all table fields with appropriate types
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      Business-friendly names from AI discovery
                    </div>
                    {optimizationLevel === 'performance' && (
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        Pre-aggregations for improved query performance
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Button
              onClick={generateCubeSchema}
              disabled={isGenerating || !selectedTable}
              size="lg"
              className="w-full"
            >
              {isGenerating ? (
                <>
                  <Zap className="h-4 w-4 mr-2 animate-pulse" />
                  Generating Cube Schema...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Generate Cube Schema
                </>
              )}
            </Button>
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="measures">Measures ({cubeSchema.measures.length})</TabsTrigger>
              <TabsTrigger value="dimensions">Dimensions ({cubeSchema.dimensions.length})</TabsTrigger>
              <TabsTrigger value="preAgg">Pre-Aggregations</TabsTrigger>
              <TabsTrigger value="preview">Schema Preview</TabsTrigger>
            </TabsList>

            <TabsContent value="measures" className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-medium">Measures</h3>
                <Button onClick={addMeasure} size="sm" variant="outline">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Measure
                </Button>
              </div>

              <div className="space-y-3">
                {cubeSchema.measures.map((measure, index) => (
                  <Card key={index}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {getMeasureIcon(measure.type)}
                          <div>
                            <div className="font-medium">{measure.title || measure.name}</div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">
                              {measure.description}
                            </div>
                            <div className="text-xs font-mono text-gray-500 mt-1">
                              {measure.name} ({measure.type})
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{measure.type}</Badge>
                          <Button variant="ghost" size="sm">
                            <Settings className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="dimensions" className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-medium">Dimensions</h3>
                <Button onClick={addDimension} size="sm" variant="outline">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Dimension
                </Button>
              </div>

              <div className="space-y-3">
                {cubeSchema.dimensions.map((dimension, index) => (
                  <Card key={index}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {getDimensionIcon(dimension.type)}
                          <div>
                            <div className="font-medium">{dimension.title || dimension.name}</div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">
                              {dimension.description}
                            </div>
                            <div className="text-xs font-mono text-gray-500 mt-1">
                              {dimension.name} ({dimension.type})
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{dimension.type}</Badge>
                          {dimension.primaryKey && (
                            <Badge variant="secondary" className="text-xs">PK</Badge>
                          )}
                          <Button variant="ghost" size="sm">
                            <Settings className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="preAgg" className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-medium">Pre-Aggregations</h3>
                {optimizationLevel === 'performance' && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    <Zap className="h-3 w-3" />
                    Performance Optimized
                  </Badge>
                )}
              </div>

              {!cubeSchema.preAggregations || cubeSchema.preAggregations.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <Clock className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No pre-aggregations configured</p>
                  <p className="text-sm">Pre-aggregations improve query performance</p>
                  <Button variant="outline" className="mt-3">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Pre-Aggregation
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {cubeSchema.preAggregations.map((preAgg, index) => (
                    <Card key={index}>
                      <CardContent className="p-4">
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Zap className="h-4 w-4 text-orange-500" />
                              <span className="font-medium">{preAgg.name}</span>
                            </div>
                            <Badge variant="outline">
                              {preAgg.granularity || 'No time grouping'}
                            </Badge>
                          </div>

                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <Label className="text-xs">Measures ({preAgg.measures.length})</Label>
                              <div className="text-gray-600 dark:text-gray-400">
                                {preAgg.measures.join(', ')}
                              </div>
                            </div>
                            <div>
                              <Label className="text-xs">Dimensions ({preAgg.dimensions.length})</Label>
                              <div className="text-gray-600 dark:text-gray-400">
                                {preAgg.dimensions.join(', ')}
                              </div>
                            </div>
                          </div>

                          {preAgg.refreshKey && (
                            <div className="text-xs text-blue-600 dark:text-blue-400">
                              Refreshes: {preAgg.refreshKey.every}
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="preview" className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-medium">Generated Schema</h3>
                <div className="flex gap-2">
                  <Button onClick={() => onPreview?.(cubeSchema)} variant="outline" size="sm">
                    <Eye className="h-4 w-4 mr-2" />
                    Preview Query
                  </Button>
                  <Button onClick={() => navigator.clipboard.writeText(generateSQLPreview())} variant="outline" size="sm">
                    <Download className="h-4 w-4 mr-2" />
                    Copy Schema
                  </Button>
                </div>
              </div>

              <Card>
                <CardContent className="p-0">
                  <pre className="p-4 text-xs bg-gray-50 dark:bg-gray-900 overflow-x-auto">
                    <code>{generateSQLPreview()}</code>
                  </pre>
                </CardContent>
              </Card>

              <div className="flex gap-3">
                <Button onClick={generateCubeSchema} variant="outline">
                  <Sparkles className="h-4 w-4 mr-2" />
                  Regenerate
                </Button>
                <Button onClick={() => onSchemaGenerated?.(cubeSchema)} className="flex-1">
                  <Play className="h-4 w-4 mr-2" />
                  Deploy Schema
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        )}
      </CardContent>
    </Card>
  );
};